/**
 * Annotation Slice — revision cloud and freehand drawing annotations with
 * optional voice/text notes.
 *
 * Annotations can be:
 * - 'cloud':   Rectangular revision cloud with wavy border.
 * - 'drawing': Freehand drawing with a solid red line.
 *
 * Each annotation can have an optional voice note and/or text note attached.
 */

export type AnnotationType = 'cloud' | 'drawing';

export interface AnnotationPoint {
  x: number; // Percentage 0-100
  y: number; // Percentage 0-100
}

export interface Annotation {
  id: string;
  number: number; // 01, 02, 03...
  type: AnnotationType; // 'cloud' or 'drawing'
  // Bounding box (percentages of image dimensions 0-100)
  x: number; // Left edge %
  y: number; // Top edge %
  width: number; // Width %
  height: number; // Height %
  // For freehand drawings — array of points as percentages
  points?: AnnotationPoint[];
  voiceNote?: string; // Audio data URL
  textNote?: string; // Transcribed or typed text note
  createdAt: string;
}

export interface AnnotationState {
  annotations: Annotation[];
  nextAnnotationNumber: number;
  isRecording: boolean;
  currentRecordingId: string | null;
  selectedAnnotationId: string | null;
}

export interface AnnotationActions {
  addAnnotation: (
    x: number,
    y: number,
    width: number,
    height: number,
    type?: AnnotationType,
    points?: AnnotationPoint[],
  ) => string;
  removeAnnotation: (id: string) => void;
  setVoiceNote: (id: string, audioDataUrl: string) => void;
  setTextNote: (id: string, text: string) => void;
  clearAnnotations: () => void;
  setIsRecording: (isRecording: boolean) => void;
  setCurrentRecordingId: (id: string | null) => void;
  setSelectedAnnotationId: (id: string | null) => void;
}

export const annotationSlice: AnnotationState = {
  annotations: [],
  nextAnnotationNumber: 1,
  isRecording: false,
  currentRecordingId: null,
  selectedAnnotationId: null,
};

/**
 * Immer recipe type for this slice. Typed against the slice's own state so we
 * can drop `any` while keeping the loose `set(fn)`/`get()` signatures the
 * composed store expects (full AppState typing here would create a circular
 * reference).
 */
type AnnotationRecipe = (state: AnnotationState) => void;

export const annotationActions = (
  set: (fn: AnnotationRecipe) => void,
  get: () => AnnotationState,
): AnnotationActions => ({
  addAnnotation: (
    x: number,
    y: number,
    width: number,
    height: number,
    type: AnnotationType = 'cloud',
    points?: AnnotationPoint[],
  ): string => {
    const id = `annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const number = get().nextAnnotationNumber;

    set((state) => {
      const annotation: Annotation = {
        id,
        number,
        type,
        x,
        y,
        width,
        height,
        createdAt: new Date().toISOString(),
      };

      // Add points for freehand drawings
      if (type === 'drawing' && points && points.length > 0) {
        annotation.points = points;
      }

      state.annotations.push(annotation);
      state.nextAnnotationNumber = number + 1;
    });

    return id;
  },

  removeAnnotation: (id: string) =>
    set((state) => {
      state.annotations = state.annotations.filter((a) => a.id !== id);
      if (state.selectedAnnotationId === id) {
        state.selectedAnnotationId = null;
      }
    }),

  setVoiceNote: (id: string, audioDataUrl: string) =>
    set((state) => {
      const annotation = state.annotations.find((a) => a.id === id);
      if (annotation) {
        annotation.voiceNote = audioDataUrl;
      }
    }),

  setTextNote: (id: string, text: string) =>
    set((state) => {
      const annotation = state.annotations.find((a) => a.id === id);
      if (annotation) {
        annotation.textNote = text;
      }
    }),

  clearAnnotations: () =>
    set((state) => {
      state.annotations = [];
      state.nextAnnotationNumber = 1;
      state.isRecording = false;
      state.currentRecordingId = null;
      state.selectedAnnotationId = null;
    }),

  setIsRecording: (isRecording: boolean) =>
    set((state) => {
      state.isRecording = isRecording;
    }),

  setCurrentRecordingId: (id: string | null) =>
    set((state) => {
      state.currentRecordingId = id;
    }),

  setSelectedAnnotationId: (id: string | null) =>
    set((state) => {
      state.selectedAnnotationId = id;
    }),
});
